import { createContext, useContext, useEffect, useState } from "react";
import { api } from "./api";

const BrandContext = createContext(null);

// Build-time fallback. CRA's DefinePlugin inlines the env var; Terser DCEs
// the unreachable branch, so the cierge bundle ships with the cierge
// fallback only (no RBT strings) and vice versa.
const FALLBACK = process.env.REACT_APP_BRAND_MODE === "cierge"
    ? {
        mode: "cierge",
        name: "Responsible Technology Leadership Lab",
        wordmark: "KATLAS · Cierge Labs",
        subtitle: "Governed leadership decisions · AI assurance · signed receipts",
        positioning: "",
        show_portfolio: false,
        show_tech_lab: false,
        show_rbt_tenets: false,
        show_scenario_cards: false,
        primary_cta_label: "Run the governed action",
        primary_cta_route: "/responsible-technology",
        footer_owner: "KATLAS · Cierge Labs",
        title_suffix: "KATLAS Cierge Labs",
    }
    : {
        mode: "rbt",
        name: "Responsible Technology Leadership Lab",
        wordmark: "RBT × KATLAS",
        subtitle: "Executive education meets live operational governance.",
        positioning: "",
        show_portfolio: true,
        show_tech_lab: true,
        show_rbt_tenets: true,
        show_scenario_cards: true,
        primary_cta_label: "Explore scenario labs",
        primary_cta_route: "/#scenarios",
        footer_owner: "Right Brain Thinking International × KATLAS Technology",
        title_suffix: "RBT × KATLAS",
    };

export function BrandProvider({ children }) {
    const [brand, setBrand] = useState(FALLBACK);
    const [ready, setReady] = useState(false);

    useEffect(() => {
        api.get("/brand")
            .then((r) => setBrand({ ...FALLBACK, ...r.data }))
            .catch(() => setBrand(FALLBACK))
            .finally(() => setReady(true));
    }, []);

    // Sync document.title and meta description per brand
    useEffect(() => {
        if (!ready) return;
        const title = `${brand.name} | ${brand.title_suffix}`;
        document.title = title;
        const meta = document.querySelector('meta[name="description"]');
        if (meta) {
            meta.setAttribute(
                "content",
                brand.mode === "cierge"
                    ? "A Cierge Labs demonstrator for responsible technology and AI governance, showing how Custody, Authority and Receipts turn recommendations into governed actions with KATLAS-signed receipts."
                    : "Executive education meets live operational governance. Leaders rehearse responsible AI, cybersecurity and sustainable technology decisions. KATLAS provides custody, authority and receipts.",
            );
        }
    }, [brand, ready]);

    return (
        <BrandContext.Provider value={{ brand, ready }}>
            {children}
        </BrandContext.Provider>
    );
}

export function useBrand() {
    const ctx = useContext(BrandContext);
    if (!ctx) throw new Error("useBrand must be used inside BrandProvider");
    return ctx.brand;
}
